import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import {
  ArrowLeft,
  UploadCloud,
  FileText,
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Clock,
  Mail,
  PartyPopper,
} from "lucide-react";
import { BulkIntakeService } from "@/services/bulkIntakeService";
import { JobService } from "@/services/jobService";
import { shortId } from "@/utils/formatText";
import useLiveResource from "@/hooks/useLiveResource";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import Skeleton from "@/components/shared/Skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import RetryButton from "@/components/shared/RetryButton";
import { droppedFileReason, droppedSummary } from "./droppedFiles";

const MAX_FILES = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const ACCEPTED_EXTENSIONS = [".pdf", ".doc", ".docx"];

const BATCH_STAGE = {
  UPLOADING: "UPLOADING",
  EXTRACTING: "EXTRACTING",
  READY_FOR_REVIEW: "READY_FOR_REVIEW",
  PENDING_EMAIL_CONFIRMATION: "PENDING_EMAIL_CONFIRMATION",
  CONFIRMED: "CONFIRMED",
};

const ITEM_STATUS_META = {
  PENDING: { label: "Queued", colorIndex: 8 },
  EXTRACTING: { label: "Extracting…", colorIndex: 4 },
  EXTRACTED: { label: "Extracted", colorIndex: 3 },
  FAILED: { label: "Failed", colorIndex: 6 },
  DISCARDED: { label: "Discarded", colorIndex: 8 },
};

const genId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const extOf = (name = "") => {
  const idx = name.lastIndexOf(".");
  return idx === -1 ? "" : name.slice(idx).toLowerCase();
};

const validateFile = (file) => {
  if (!ACCEPTED_EXTENSIONS.includes(extOf(file.name))) {
    return "Only PDF or DOCX files are accepted.";
  }
  return null;
};

const BulkUpload = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(true);
  const setHeader = useOutletContext();

  const [batch, setBatch] = useState(null); // { batch_id, status, item_count }
  const [batchLoading, setBatchLoading] = useState(true);
  const [batchError, setBatchError] = useState(null);

  const [items, setItems] = useState([]); // local rows, keyed by clientId, merged with item_id once uploaded
  const [isDragging, setIsDragging] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [uploadWarnings, setUploadWarnings] = useState({ limit: 0, duplicate: 0, oversized: 0 });

  // The compliance wording is the server's, and only the server's. There is
  // no client-side default on purpose — see the fetch below.
  const [consentText, setConsentText] = useState("");
  const [consentTextFailed, setConsentTextFailed] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [startingExtraction, setStartingExtraction] = useState(false);
  const [startExtractionError, setStartExtractionError] = useState(null);
  const [itemActionError, setItemActionError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmEmailEditing, setConfirmEmailEditing] = useState(false);
  // Only ever held while the address is being edited — `/auth/email`
  // verifies it, and it is cleared the moment the call returns.
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [confirmSentTo, setConfirmSentTo] = useState(null);
  // null until the job has been read. Decides whether "Confirm all &
  // submit" opens the email modal at all — with the email step off there is no
  // address to collect. The response still decides what actually happened.
  const [emailConsentRequired, setEmailConsentRequired] = useState(null);
  const successfullyUploadedCount = items.filter((it) => it.item_id && !it.uploadError).length;
  const limitReached = successfullyUploadedCount >= MAX_FILES;

  const [finalResult, setFinalResult] = useState(null); // { confirmed_count, dropped_count, dropped_files }

  const clearUploadWarnings = () => setUploadWarnings({ limit: 0, duplicate: 0, oversized: 0 });

    useEffect(() => {
    
        setHeader({
          title: "Bulk Upload Resumes",
        });
      }, []);

  // ── Load job header + create the batch ─────────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    (async () => {
      try {
        const res = await JobService.fetchJobById(jobId);
        setJob(res?.data ?? null);
        // The consent flag is per job now, so it rides on the response this
        // effect already has rather than on a second, org-wide request. Same
        // fail-safe as before: anything other than an explicit `false` means the
        // email step is on, because a wasted click beats stranding a batch that
        // really does need an address.
        setEmailConsentRequired(res?.data?.require_bulk_email_consent !== false);
      } catch (err) {
        console.error("Failed to fetch job for bulk upload:", err);
        setEmailConsentRequired(true);
      } finally {
        setJobLoading(false);
      }
    })();
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await BulkIntakeService.createBatch(jobId);
        if (!cancelled) setBatch(res);
      } catch (err) {
        const code = err?.response?.data?.error_code;
        if (!cancelled) {
          setBatchError(
            code === "BULK_INTAKE_JOB_CLOSED"
              ? "This job is no longer accepting applications, so a bulk upload can't be started."
              : "Couldn't start a bulk upload batch. Please try again."
          );
        }
        console.error("Failed to create bulk intake batch:", err);
      } finally {
        if (!cancelled) setBatchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  /**
   * The recruiter attestation wording, from the server.
   *
   * There is no fallback string here, and there must not be one. The wording
   * is a compliance statement the recruiter is asked to affirm: it says what
   * they are attesting to, and the tenant is held to it. A short sentence
   * written in the client and shown "just in case the fetch failed" is a
   * different statement from the one on file, and nobody looking at the screen
   * can tell which one they agreed to. That is worse than showing nothing.
   *
   * So a failed fetch offers a retry and withholds the checkbox entirely,
   * which also blocks extraction — correct, because consent is what authorises
   * it and no consent was recorded.
   */
  const loadConsentText = useCallback(() => {
    setConsentTextFailed(false);
    BulkIntakeService.getConsentText()
      .then((data) => {
        const text = data?.attestation_text ?? "";
        setConsentText(text);
        setConsentTextFailed(!text);
      })
      .catch((err) => {
        console.error("Failed to fetch consent text:", err);
        setConsentText("");
        setConsentTextFailed(true);
      });
  }, []);

  useEffect(() => {
    loadConsentText();
  }, [loadConsentText]);

const handleClearAll = useCallback(async () => {
  setClearingAll(true);
  setItemActionError(null);
  try {
    items.forEach((it) => it.item_id === null && it.controller?.abort());
    const itemsWithId = items.filter((it) => it.item_id);
    await Promise.all(itemsWithId.map((it) => BulkIntakeService.discardItem(it.item_id)));

    setItems([]);
    clearUploadWarnings();
    setConsentChecked(false);
  } catch (err) {
    setItemActionError(err?.response?.data?.message || "Couldn't remove every file. Please try again.");
    console.error("Failed to clear all items:", err);
  } finally {
    setClearingAll(false);
  }
}, [items]);


  // ── Live extraction updates ─────────────────────────────────────────────
  //
  // The four per-item events are one throttled `bulk_batch.progress` now, and
  // it carries counts rather than the item. So the screen re-reads the batch:
  // `GET /bulk-intake/{batch_id}` already serves every item's status, draft,
  // quality, error and retry state — the payload was a second copy of it.
  //
  // The confirmed batch's summary comes from the same read: `confirmed_count`,
  // `dropped_count` and `dropped_files` live on that response beside the
  // finalize response's own, so a reload and a second tab both show it.
  const syncBatch = useCallback(async () => {
    const batchId = batch?.batch_id;
    if (!batchId) return;
    try {
      const data = await BulkIntakeService.getBatch(batchId);
      setBatch((prev) => ({ ...prev, status: data.status, item_count: data.item_count }));
      setItems((prev) =>
        (data.items ?? []).map((serverItem) => {
          // Keep the client-only fields — the upload progress bar and the
          // File handle — which the server has never seen.
          const local = prev.find((it) => it.item_id === serverItem.item_id);
          return { ...local, ...serverItem };
        }),
      );
      if (data.status === BATCH_STAGE.CONFIRMED) {
        setFinalResult({
          confirmed_count: data.confirmed_count ?? 0,
          dropped_count: data.dropped_count ?? 0,
          dropped_files: data.dropped_files ?? [],
        });
      }
    } catch (err) {
      console.error("Failed to re-read the batch:", err);
    }
  }, [batch?.batch_id]);

  useLiveResource({
    keys: `bulk_batch:${batch?.batch_id}`,
    enabled: Boolean(batch?.batch_id),
    onChange: syncBatch,
  });

  // ── File selection + upload ─────────────────────────────────────────────
  const uploadOneFile = useCallback(
    async (row) => {
      try {
        const res = await BulkIntakeService.uploadResumeItem(batch.batch_id, row.file, {
          signal: row.controller?.signal,
          onUploadProgress: (evt) => {
            const progress = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
            setItems((prev) => prev.map((it) => (it.clientId === row.clientId ? { ...it, progress } : it)));
          },
        });
        setItems((prev) =>
          prev.map((it) =>
            it.clientId === row.clientId
              ? { ...it, item_id: res.item_id, status: res.status, progress: 100, uploadError: null }
              : it
          )
        );
      } catch (err) {
        // Removed mid-upload (X clicked) — the row is already gone from state,
        // nothing to surface.
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;

        const errData = err?.response?.data;
        const message =
          errData?.error_code === "BULK_INTAKE_DUPLICATE_EXISTING_CANDIDATE" && errData?.data?.candidate_id
            ? "Already applied to this job — see the existing candidate."
            : errData?.message || "Upload failed. Please try again.";
        setItems((prev) =>
          prev.map((it) =>
            it.clientId === row.clientId
              ? {
                  ...it,
                  uploadError: message,
                  duplicateCandidateId: errData?.data?.candidate_id ?? null,
                  duplicateJobId: errData?.data?.job_id ?? null,
                }
              : it
          )
        );
        console.error("Failed to upload resume item:", err);
      }
    },
    [batch?.batch_id]
  );

const handleFilesSelected = useCallback(
  (fileList) => {
    if (!batch?.batch_id || batch.status !== BATCH_STAGE.UPLOADING) return;

    const incoming = Array.from(fileList);
    // Slots are consumed by files that actually occupy the batch (uploaded
    // or still uploading) — a row that failed (duplicate, oversized, etc.)
    // never became a real item, so it shouldn't count against the 10 max.
    const activeCount = items.filter((it) => !it.uploadError).length;
    const remainingSlots = MAX_FILES - activeCount;
    const withinLimit = incoming.slice(0, Math.max(remainingSlots, 0));
    const limit = incoming.length - withinLimit.length;

    const existingNames = new Set(items.map((it) => it.file_name.toLowerCase()));
    const seenInThisDrop = new Set();

    const accepted = [];
    let duplicate = 0;
    let oversized = 0;

    withinLimit.forEach((file) => {
      const nameLower = file.name.toLowerCase();

      if (existingNames.has(nameLower) || seenInThisDrop.has(nameLower)) {
        duplicate += 1;
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        oversized += 1;
        return;
      }

      seenInThisDrop.add(nameLower);
      accepted.push(file);
    });

    setUploadWarnings({ limit, duplicate, oversized });

    const newRows = accepted.map((file) => {
      const clientError = validateFile(file);
      return {
        clientId: genId(),
        item_id: null,
        file,
        file_name: file.name,
        size: file.size,
        status: clientError ? "CLIENT_INVALID" : "PENDING",
        progress: 0,
        uploadError: clientError,
        controller: clientError ? null : new AbortController(),
      };
    });

    setItems((prev) => [...prev, ...newRows]);
    newRows.filter((r) => !r.uploadError).forEach((row) => uploadOneFile(row));
  },
  [batch?.batch_id, batch?.status, items, uploadOneFile]
);
const removeLocalItem = useCallback(async (row) => {
  if (!row.item_id) row.controller?.abort();

  const willBeEmpty = items.length === 1;

  setItems((prev) => prev.filter((it) => it.clientId !== row.clientId));

  if (willBeEmpty) {
    clearUploadWarnings();
    setItemActionError(null);
    setStartExtractionError(null);
    setConsentChecked(false);

    // Optional: reset confirmation flow state too
    setConfirmError(null);
    setConfirmSentTo(null);
    setConfirmOpen(false);
  }

  if (row.item_id) {
    try {
      await BulkIntakeService.discardItem(row.item_id);
    } catch (err) {
      setItemActionError(
        `Couldn't remove "${row.file_name}" on the server — it may still be processed.`
      );
      console.error("Failed to discard item during removal:", err);
    }
  }
}, [items]);

  const discardReviewedItem = useCallback(async (row) => {
    try {
      await BulkIntakeService.discardItem(row.item_id);
      setItems((prev) => prev.map((it) => (it.item_id === row.item_id ? { ...it, status: "DISCARDED" } : it)));
    } catch (err) {
      console.error("Failed to discard item:", err);
    }
  }, []);

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      handleFilesSelected(e.dataTransfer.files);
    },
    [handleFilesSelected]
  );

  // ── Consent → start extraction ──────────────────────────────────────────
  
  const stillUploading = items.some((it) => it.item_id === null && !it.uploadError);

const handleStartExtraction = useCallback(async () => {
  if (!batch?.batch_id || !consentChecked) return;
  setStartingExtraction(true);
  setStartExtractionError(null);
  try {
    await BulkIntakeService.giveBatchConsent(batch.batch_id);
    navigate(`/admin/job-detail/${jobId}/bulk-review/${batch.batch_id}`);
  } catch (err) {
    setStartExtractionError(err?.response?.data?.message || "Couldn't start extraction. Please try again.");
    console.error("Failed to start extraction:", err);
  } finally {
    setStartingExtraction(false);
  }
}, [batch?.batch_id, consentChecked, jobId, navigate]);

  // ── Confirm-all flow ─────────────────────────────────────────────────────
  const readyItems = items.filter((it) => it.status === "EXTRACTED");
  const openConfirmModal = useCallback(async () => {
    setConfirmOpen(true);
    setConfirmError(null);
    setConfirmSentTo(null);
    try {
      const { email } = await BulkIntakeService.getAccountEmail();
      setConfirmEmail(email ?? "");
      setConfirmEmailEditing(!email);
    } catch (err) {
      console.error("Failed to fetch account email:", err);
    }
  }, []);

  const submitBatch = useCallback(
    async (email) => {
      // Keep sending the email even though it's ignored when this job has its
      // bulk email step off — the pre-read flag can be stale if someone edits
      // the job mid-session, and in two-factor mode it's still verified
      // against the account email.
      const res = await BulkIntakeService.requestBatchConfirmation(batch.batch_id, email);

      if (res?.email_confirmation_required !== false) {
        setConfirmSentTo(res.email_masked);
        setBatch((prev) => (prev ? { ...prev, status: BATCH_STAGE.PENDING_EMAIL_CONFIRMATION } : prev));
      } else {
        // Single-factor: this call WAS the finalize — the candidates already
        // exist and the response carries the counts /confirm/finalize returns.
        setConfirmOpen(false);
        setFinalResult({
          confirmed_count: res.confirmed_count ?? 0,
          dropped_count: res.dropped_count ?? 0,
          dropped_files: res.dropped_files ?? [],
        });
        setBatch((prev) => (prev ? { ...prev, status: BATCH_STAGE.CONFIRMED } : prev));
      }
    },
    [batch?.batch_id]
  );

  const confirmErrorMessage = (err) => {
    const code = err?.response?.data?.error_code;
    return code === "BULK_INTAKE_NO_READY_ITEMS"
      ? "Every resume in this batch failed or was discarded — there's nothing left to submit."
      : err?.response?.data?.message || "Couldn't submit this batch. Please try again.";
  };

  const handleSendConfirmation = useCallback(async () => {
    if (!batch?.batch_id) return;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      if (confirmEmailEditing) {
        await BulkIntakeService.updateAccountEmail(confirmEmail, confirmPassword);
        setConfirmPassword("");
        setConfirmEmailEditing(false);
      }
      const { email } = await BulkIntakeService.getAccountEmail();
      await submitBatch(email);
    } catch (err) {
      setConfirmError(confirmErrorMessage(err));
      console.error("Failed to request batch confirmation:", err);
    } finally {
      setConfirmLoading(false);
    }
  }, [batch?.batch_id, confirmEmail, confirmEmailEditing, confirmPassword, submitBatch]);

  const handleConfirmAll = useCallback(async () => {
    if (!batch?.batch_id) return;

    // Two-factor: collect/confirm the destination address first.
    if (emailConsentRequired !== false) {
      openConfirmModal();
      return;
    }

    // Single-factor: no mail is sent, so there is no address to collect — go
    // straight to the completion screen without the email modal.
    setConfirmLoading(true);
    setConfirmError(null);
    setConfirmSentTo(null);
    try {
      const { email } = await BulkIntakeService.getAccountEmail().catch(() => ({ email: null }));
      await submitBatch(email);
    } catch (err) {
      setConfirmError(confirmErrorMessage(err));
      console.error("Failed to submit batch:", err);
    } finally {
      setConfirmLoading(false);
    }
  }, [batch?.batch_id, emailConsentRequired, openConfirmModal, submitBatch]);

  // ── Render helpers ───────────────────────────────────────────────────────
  const stage = batch?.status;

  if (batchError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
        <AlertCircle className="text-red-500" size={28} />
        <p className="text-text-primary text-sm max-w-sm">{batchError}</p>
        <Button
          onClick={() => navigate(`/admin/job-detail/${jobId}`)}
          className="text-xs text-text-blue hover:underline"
        >
          Back to job
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" style={{ zoom: 0.95 }}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          onClick={() => navigate(`/admin/job-detail/${jobId}`)}
          className="flex items-center gap-1 text-text-secondary border border-border-primary rounded-lg bg-background-tertiary hover:text-text-primary text-[13px]"
        >
          <ArrowLeft size={14} /> Back to job
        </Button>
      </div>

      <div className="rounded-xl border border-border-primary bg-background-secondary p-4 sm:p-5">
        {jobLoading ? (
          <Skeleton className="h-6 w-64" />
        ) : (
          <>
            <h1 className="text-lg sm:text-xl font-semibold text-text-primary leading-tight">
              Bulk Upload Resumes
            </h1>
            <p className="text-xs text-text-secondary mt-1">
              For <span className="text-text-primary font-medium">{job?.title ?? "this job"}</span>
              {(job?.id ?? jobId) != null && (
                <span className="text-text-secondary font-mono text-[11px]"> ({shortId(job?.id ?? jobId)})</span>
              )}{" "}
              — upload up to {MAX_FILES} resumes, review the AI-extracted details, then submit them all at once.
            </p>
          </>
        )}
      </div>

      {finalResult ? (
        <div className="rounded-xl border border-border-primary bg-background-secondary p-8 flex flex-col items-center text-center gap-3">
          <PartyPopper className="text-text-blue" size={28} />
          <h2 className="text-text-primary font-semibold text-base">Batch submitted successfully</h2>
          <p className="text-text-secondary text-sm max-w-md">
            {finalResult.confirmed_count} of {finalResult.confirmed_count + finalResult.dropped_count} candidates
            were added to this job.
            {finalResult.dropped_count > 0 && ` ${droppedSummary(finalResult.dropped_count)}`}
          </p>
          {finalResult.dropped_files?.length > 0 && (
            <ul className="text-xs text-text-secondary text-left w-full max-w-md list-disc pl-5 space-y-0.5">
              {finalResult.dropped_files.map((f, i) => (
                <li key={i}>
                  {f.file_name} — {droppedFileReason(f)}
                </li>
              ))}
            </ul>
          )}
          <Button
            onClick={() => navigate(`/admin/job-detail/${jobId}`)}
            className="mt-2 text-xs bg-text-blue text-white rounded-lg px-4 py-2"
          >
            View candidates
          </Button>
        </div>
      ) : batchLoading ? (
        <div className="rounded-xl border border-border-primary bg-background-secondary p-6">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : stage === BATCH_STAGE.PENDING_EMAIL_CONFIRMATION ? (
        <div className="rounded-xl border border-border-primary bg-background-secondary p-8 flex flex-col items-center text-center gap-3">
          <Mail className="text-text-blue" size={28} />
          <h2 className="text-text-primary font-semibold text-base">Check your inbox</h2>
          <p className="text-text-secondary text-sm max-w-md">
            We sent a confirmation link to {confirmSentTo ?? "your email"}. Click it to finish submitting{" "}
            {readyItems.length} candidate{readyItems.length !== 1 ? "s" : ""} for this job.
          </p>
        </div>
      ) : (
        <>
          {/* Upload dropzone — only while UPLOADING */}
          {stage === BATCH_STAGE.UPLOADING && (
  <div
    onDragOver={(e) => {
      e.preventDefault();
      if (!limitReached) setIsDragging(true);
    }}
    onDragLeave={() => setIsDragging(false)}
    onDrop={(e) => {
      e.preventDefault();
      if (limitReached) return;
      onDrop(e);
    }}
    className={`rounded-xl border-2 border-dashed p-8 flex flex-col items-center justify-center gap-2 text-center transition-colors ${
      limitReached
        ? "border-border-primary bg-background-icon/10 opacity-50 cursor-not-allowed"
        : isDragging
        ? "border-text-blue bg-background-icon/20 cursor-pointer"
        : "border-border-primary bg-background-secondary cursor-pointer"
    }`}
    onClick={() => {
      if (!limitReached) fileInputRef.current?.click();
    }}
  >
    <UploadCloud className="text-text-secondary" size={26} />
    <p className="text-text-primary text-sm font-medium">
      {limitReached ? `File limit reached (${MAX_FILES} max)` : "Drag and drop resumes, or click to browse"}
    </p>
    <p className="text-text-secondary text-xs">
      {limitReached
        ? "Remove a file below to add more."
        : `PDF, DOC, or DOCX — up to 20MB each, ${MAX_FILES} max`}
    </p>
    <Input
      ref={fileInputRef}
      type="file"
      multiple
      accept={ACCEPTED_EXTENSIONS.join(",")}
      className="hidden"
      disabled={limitReached}
      onChange={(e) => {
        handleFilesSelected(e.target.files);
        e.target.value = "";
      }}
    />
  </div>
)}

{(uploadWarnings.limit > 0 || uploadWarnings.duplicate > 0 || uploadWarnings.oversized > 0) && (
  <div className="flex items-center justify-between gap-2 rounded-lg border border-tone-rose bg-tone-rose-soft px-4 py-2.5 text-tone-rose">
    <ul className="text-xs list-disc pl-4 space-y-0.5">
      {uploadWarnings.limit > 0 && (
        <li>
          {uploadWarnings.limit} file{uploadWarnings.limit !== 1 ? "s" : ""} not added — {MAX_FILES} file limit reached.
        </li>
      )}
      {uploadWarnings.duplicate > 0 && (
        <li>
          {uploadWarnings.duplicate} file{uploadWarnings.duplicate !== 1 ? "s" : ""} skipped — already added to this batch.
        </li>
      )}
      {uploadWarnings.oversized > 0 && (
        <li>
          {uploadWarnings.oversized} file{uploadWarnings.oversized !== 1 ? "s" : ""} skipped — over the 20MB size limit.
        </li>
      )}
    </ul>
    <Button onClick={clearUploadWarnings} className="text-tone-rose shrink-0">
      <X size={14} />
    </Button>
  </div>
)}
          {itemActionError && (
            <div className="rounded-lg border border-tone-rose bg-tone-rose-soft px-4 py-2.5 text-xs text-tone-rose">
              {itemActionError}
            </div>
          )}

          {/* File / item list */}
          {items.length > 0 && (
             <div className="rounded-xl border border-border-primary bg-background-secondary overflow-hidden">
    <div className="flex items-center justify-between px-4 py-3 border-b border-border-primary">
      <p className="text-xs text-text-secondary">
        {items.length} file{items.length !== 1 ? "s" : ""}
        {stage === BATCH_STAGE.UPLOADING && ` · ${successfullyUploadedCount} uploaded`}
      </p>
      {stage === BATCH_STAGE.UPLOADING && (
        <Button
          onClick={handleClearAll}
          disabled={clearingAll || stillUploading}
          className="text-[11px] text-red-500 disabled:opacity-50 shrink-0"
        >
          {clearingAll ? "Clearing…" : "Clear all"}
        </Button>
      )}
    </div>
              
              <ul className="divide-y divide-border-primary max-h-96 overflow-y-auto">
                {items.map((row) => {
                  const meta = ITEM_STATUS_META[row.status];
                  return (
                    <li key={row.clientId} className="flex items-center gap-3 px-4 py-2.5">
                      <FileText className="text-text-secondary shrink-0" size={16} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] text-text-primary truncate">{row.file_name}</p>
                        {row.uploadError ? (
                          <p className="text-[11px] text-red-500">
                            {row.uploadError}
                            {row.duplicateCandidateId && (
                              <>
                                {" "}
                                <a
                                  href={`/admin/candidate-analysis/${row.duplicateCandidateId}?job_id=${row.duplicateJobId ?? jobId}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="underline"
                                >
                                  View candidate
                                </a>
                              </>
                            )}
                          </p>
                        ) : row.status === "FAILED" && row.extraction_error ? (
                          <p className="text-[11px] text-red-500">{row.extraction_error}</p>
                        ) : row.size ? (
                          <p className="text-[11px] text-text-secondary">{formatBytes(row.size)}</p>
                        ) : null}
                        {row.item_id === null && !row.uploadError && (
                          <div className="mt-1 h-1 w-full max-w-[160px] rounded-full bg-background-icon/40 overflow-hidden">
                            <div
                              className="h-full bg-text-blue transition-all"
                              style={{ width: `${row.progress ?? 0}%` }}
                            />
                          </div>
                        )}
                      </div>

                      {(stage === BATCH_STAGE.UPLOADING || row.status === "EXTRACTED") && row.status !== "DISCARDED" && (
                        <Button
                          onClick={() =>
                            stage === BATCH_STAGE.UPLOADING ? removeLocalItem(row) : discardReviewedItem(row)
                          }
                          className="text-text-secondary hover:text-red-500 shrink-0"
                        >
                          <X size={15} />
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Consent + start extraction */}
          {stage === BATCH_STAGE.UPLOADING && items.length > 0 && (
            <div className="rounded-xl border border-border-primary bg-background-secondary p-4 flex flex-col gap-3">
              {consentTextFailed ? (
                // No wording, no checkbox. Substituting our own sentence here
                // would ask the recruiter to attest to something other than
                // what the tenant's compliance text actually says.
                <div className="flex flex-col items-start gap-2">
                  <p className="text-xs text-text-secondary">
                    The consent wording could not be loaded, so this step cannot be confirmed yet. Your uploaded files are safe — retry to continue.
                  </p>
                  <RetryButton onClick={loadConsentText} className="text-xs" />
                </div>
              ) : (
                <label htmlFor="bulk-upload-consent" className="flex items-start gap-2 text-xs text-text-secondary cursor-pointer">
                  <Checkbox
                    id="bulk-upload-consent"
                    checked={consentChecked}
                    onCheckedChange={(checked) => setConsentChecked(checked === true)}
                    disabled={!consentText}
                    className="mt-0.5"
                  />
                  <span>{consentText || "Loading the consent wording…"}</span>
                </label>
              )}
              <Button
                onClick={handleStartExtraction}
                disabled={!consentText || !consentChecked || successfullyUploadedCount === 0 || stillUploading || startingExtraction}
                className="self-start text-xs bg-text-blue text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {startingExtraction ? "Starting…" : `Start extraction (${successfullyUploadedCount})`}
              </Button>
              {startExtractionError && <p className="text-[11px] text-red-500">{startExtractionError}</p>}
            </div>
          )}

          {/* Extracting state */}
          {stage === BATCH_STAGE.EXTRACTING && (
            <div className="flex items-center gap-2 text-text-secondary text-xs px-1">
              <Loader2 className="animate-spin" size={14} /> Extracting resumes — this list will update as each one
              finishes.
            </div>
          )}

          {/* Review → confirm all */}
          {stage === BATCH_STAGE.READY_FOR_REVIEW && (
            <div className="flex items-center justify-between rounded-xl border border-border-primary bg-background-secondary p-4">
              <p className="text-xs text-text-secondary">
                {readyItems.length} resume{readyItems.length !== 1 ? "s" : ""} ready to submit. Discard any you don't
                want, then confirm all.
                {confirmError && !confirmOpen && <span className="block text-red-500 mt-1">{confirmError}</span>}
              </p>
              <Button
                onClick={handleConfirmAll}
                disabled={readyItems.length === 0 || emailConsentRequired === null || confirmLoading}
                className="text-xs bg-text-blue text-white rounded-lg px-4 py-2 disabled:opacity-50 shrink-0"
              >
                {confirmLoading ? "Submitting…" : "Confirm all & submit"}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Confirm-all email modal */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="border-b-0 pb-0">
            <DialogTitle>Confirm all candidates</DialogTitle>
            <DialogDescription>
              {confirmSentTo
                ? `A fresh confirmation link has been sent to ${confirmSentTo}.`
                : "We'll email you a link to confirm before any candidates are created."}
            </DialogDescription>
          </DialogHeader>

          {!confirmSentTo && (
            <div className="px-6 pt-3 pb-2 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={confirmEmail}
                  disabled={!confirmEmailEditing}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 text-[13px] bg-background-primary px-3 py-2 disabled:opacity-70"
                />
                {!confirmEmailEditing && (
                  <Button
                    onClick={() => setConfirmEmailEditing(true)}
                    className="text-[11px] text-text-blue hover:underline shrink-0"
                  >
                    Edit
                  </Button>
                )}
              </div>
              {confirmEmailEditing && (
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Current password"
                  className="text-[13px] bg-background-primary px-3 py-2"
                />
              )}
              {confirmError && <p className="text-[11px] text-red-500">{confirmError}</p>}
            </div>
          )}

          <DialogFooter className="border-t-0 pt-2">
            {confirmSentTo ? (
              <Button
                onClick={() => {
                  setConfirmOpen(false);
                  navigate("/admin/jobs");
                }}
                className="text-xs bg-text-blue text-white rounded-lg px-4 py-2"
              >
                Back to jobs
              </Button>
            ) : (
              <Button
                onClick={handleSendConfirmation}
                disabled={confirmLoading || !confirmEmail || (confirmEmailEditing && !confirmPassword)}
                className="text-xs bg-text-blue text-white rounded-lg px-4 py-2 disabled:opacity-50"
              >
                {confirmLoading ? "Sending…" : "Send confirmation link"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkUpload;